import { WagmiConfig, createConfig } from 'wagmi'
import { BrowserRouter } from 'react-router-dom'
import { Routes, Route, useLocation, useNavigate, Outlet } from 'react-router-dom'
import { AppLayout } from './components/AppLayout'

import { Marketplace } from './components/Marketplace'
import { SystemStatus } from './components/SystemStatus'
import { AdminPanel } from './components/AdminPanel'
import { LandingPage } from './components/LandingPage'
import { ThemeProvider } from './contexts/ThemeContext'
import { TrinityProvider } from './contexts/TrinityContext'
import { createPublicClient, http } from 'viem'
import { About } from './components/About'
import { Create } from './pages/Create'
import { Accounts } from './components/Accounts'
import { TrinityMaster } from './components/TrinityMaster'
import { useState, useEffect } from 'react'
import { GeoGate } from './components/GeoGate'
import { GEO_CONFIG } from './config/geo'
import { AdminRoute } from './components/AdminRoute'
import { ProductProvider } from './contexts/ProductContext'
import { useAccount } from 'wagmi'

// Components
import { RainbowKitProvider, darkTheme, getDefaultWallets } from '@rainbow-me/rainbowkit'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { NETWORK_CONFIG } from './config'
import '@rainbow-me/rainbowkit/styles.css'
import { useAdmin } from './hooks/useAdmin'
import Products from './pages/Products'
import EntityPage from './pages/EntityPage'
import ProductPage from './components/ProductPage'
import { TrinityBondProvider } from './contexts/TrinityBondContext'

const chains = [NETWORK_CONFIG]

const { connectors } = getDefaultWallets({
  appName: 'Trinity Bond System',
  projectId: import.meta.env.VITE_WALLETCONNECT_PROJECT_ID || 'YOUR_PROJECT_ID',
  chains,
})

const publicClient = createPublicClient({
  chain: NETWORK_CONFIG,
  transport: http()
})

const wagmiConfig = createConfig({
  autoConnect: true,
  connectors,
  publicClient
})

// Create a client
const queryClient = new QueryClient()

function PasswordGate() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const { adminEnabled, setAdminEnabled } = useAdmin();
  const navigate = useNavigate();
  const location = useLocation();

  // Check authentication on mount
  useEffect(() => {
    const auth = localStorage.getItem('trinity_auth');
    const isAdmin = localStorage.getItem('trinity_is_admin');
    
    if (auth === 'true') {
      setIsAuthenticated(true);
      setAdminEnabled(isAdmin === 'true');
      // Don't redirect if already authenticated - let the user stay on their intended route
    }
  }, [setAdminEnabled]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password === 'dragonboo') {
      setIsAuthenticated(true);
      setAdminEnabled(true);
      localStorage.setItem('trinity_auth', 'true');
      localStorage.setItem('trinity_is_admin', 'true');
      setError('');
      navigate('/');
    } else if (password === 'trinity2025') {
      setIsAuthenticated(true);
      setAdminEnabled(false);
      localStorage.setItem('trinity_auth', 'true');
      localStorage.setItem('trinity_is_admin', 'false');
      setError('');
      navigate('/');
    } else {
      setError('Invalid password');
      setIsAuthenticated(false);
      setAdminEnabled(false);
      localStorage.removeItem('trinity_auth');
      localStorage.removeItem('trinity_is_admin');
    }
  };

  console.log('PasswordGate');
  console.log("App.tsx isAuthenticated", isAuthenticated);
  console.log("App.tsx adminEnabled", adminEnabled);
  console.log("App.tsx location.pathname", location.pathname);
  console.log("App.tsx adminEnabled", adminEnabled);
  
  // If already authenticated, render outlet
  if (isAuthenticated) {
    return <Outlet />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-black flex items-center justify-center">
      <div className="w-full max-w-md">
        <form onSubmit={handleSubmit} className="glass-panel p-8 rounded-lg shadow-xl">
          <div className="text-center mb-8">
            <span className="text-3xl">†</span>
            <h2 className="text-2xl font-bold text-white mb-2">Trinity</h2>
          </div>
          <div className="space-y-4">
            <div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="w-full px-4 py-2 bg-black/40 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              />
            </div>
            {error && (
              <div className="text-red-500 text-sm">{error}</div>
            )}
            <button
              type="submit"
              className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 rounded-lg text-white font-medium transition-colors"
            >
              Login
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AppContent() {
  const location = useLocation();
  const isLandingPage = location.pathname === '/';

  return (
    <div className={isLandingPage ? '' : 'min-h-screen p-4 md:p-8'}>
      <Routes>
        <Route element={<PasswordGate />}>
          <Route path="/" element={<LandingPage />} />
          <Route element={<AppLayout />}>
            {/* Public Routes */}
            <Route path="/marketplace" element={<Marketplace />} />
            <Route path="/accounts" element={<Accounts />} />
            <Route path="/about" element={<About />} />
            <Route path="/products" element={<Products />} />
            <Route path="/product/:address" element={<ProductPage />} />
            <Route path="/entity/:entityAddress" element={<EntityPage />} />
            <Route path="/create" element={<Create />} />

            {/* Protected Admin Routes */}
            <Route element={<AdminRoute />}>
              <Route path="/system" element={<SystemStatus />} />
              <Route path="/admin" element={<AdminPanel />} />
              <Route path="/trinity-master" element={<TrinityMaster />} />
            </Route>
          </Route>
        </Route>
      </Routes>
    </div>
  );
}

function App() {
  return (
    <ProductProvider>
      <WagmiConfig config={wagmiConfig}>
        <QueryClientProvider client={queryClient}>
          <RainbowKitProvider
            chains={chains}
            theme={darkTheme({
              accentColor: '#10b981',
              borderRadius: 'large',
            })}
          >
            <ThemeProvider>
              <TrinityProvider>
                <TrinityBondProvider>
                  <GeoGate allowedCountries={GEO_CONFIG.allowedCountries}>
                    <BrowserRouter>
                      <AppContent />
                    </BrowserRouter>
                  </GeoGate>
                </TrinityBondProvider>
              </TrinityProvider>
            </ThemeProvider>
          </RainbowKitProvider>
        </QueryClientProvider>
      </WagmiConfig>
    </ProductProvider>
  );
}

export default App;
